import {
  upgradePricing,
  manualPricing,
  changeCustom,
  pricingStatus,
  editProfitTarget,
} from "./calculator";
import CustomCalculationsDrawer from "./CustomCalculationsDrawer";
import { removeSourceRow, inheritLaborTime } from "./helpers";
import TextField from "./CustomTextField";
import { customHeaderFieldSx } from "./customStyles";
import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  Tooltip,
  MenuItem,
  Menu,
  Typography,
} from "@mui/material";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useQuery } from "@apollo/client";
import MDButton from "components/MDButton";
import UiCard from "components/base/UiCard";
import UiButton from "components/base/UiButton";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import CustomScopeEditor, { CustomScopeEditorHandle } from "./CustomScopeEditor";
import CustomProductTypeSelect from "./CustomProductTypeSelect";
import CustomCostSection from "./CustomCostSection";
import {
  customFieldSx,
  customNumberProps,
  customSellingGrid,
  customCalculatedFieldSx,
} from "./customStyles";
import { renderCustomScopeHtml, scopePlaceholders } from "./scope";
import { SETTINGS } from "./api";
import {
  Custom,
  CostRow,
  customFinancials,
  rowQuantity,
  decimalDollars,
  formatDollarAmount,
  resetCustomPricing,
  money,
  customInput,
  sellingUnits,
  sellingUnitLabel,
  measurementUnit,
} from "./calculator";

function EditorCard({
  libraryForm,
  children,
  sx,
}: React.ComponentProps<typeof UiCard> & { libraryForm: boolean }) {
  return libraryForm ? (
    <Card sx={sx}>{children}</Card>
  ) : (
    <UiCard variant="section" sx={sx}>
      {children}
    </UiCard>
  );
}

type Props = {
  presentation?: "proposal" | "library-form";
  productTypeName?: string;
  initial: Custom;
  onSave: (item: Custom, discountCents?: string) => Promise<void>;
  discountCents?: string;
  allowDiscount?: boolean;
  initiallyExpanded?: boolean;
  sharedColumnHeaders?: boolean;
  readOnly?: boolean;
  onSaveProduct?: (item: Custom, productTypeId: string) => Promise<void>;
  onUpdateProduct?: (item: Custom) => Promise<string>;
  saveLabel?: string;
  actions?: (close: () => void) => React.ReactNode;
  optionalControl?: React.ReactNode;
  onDirtyChange?: (dirty: boolean) => void;
  onSaveStatusChange?: (status: "pending" | "fulfilled" | "rejected") => void;
  registerSave?: (save: () => Promise<{ status: boolean; data: null }>) => void;
};
export default function CustomEditor({
  presentation = "proposal",
  productTypeName,
  initial,
  onSave,
  readOnly = false,
  onSaveProduct,
  onUpdateProduct,
  saveLabel = "Save line item",
  onDirtyChange,
  registerSave,
  onSaveStatusChange,
  actions,
  optionalControl,
  discountCents = "0",
  allowDiscount = false,
  initiallyExpanded = false,
  sharedColumnHeaders = false,
}: Props) {
  const libraryForm = presentation === "library-form";
  const scopeRef = useRef<CustomScopeEditorHandle | null>(null);
  const [updatingLibrary, setUpdatingLibrary] = useState(false);
  const [calculationsOpen, setCalculationsOpen] = useState<{
    tab: number;
    rowIndex?: number;
  } | null>(null);
  const [deletingRow, setDeletingRow] = useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [item, setItem] = useState<Custom>(() =>
    customInput(
      upgradePricing(
        {
          ...initial,
          internalName: initial.internalName || (libraryForm ? initial.name : null),
          calculationVersion: 2,
        },
        decimalDollars(discountCents)
      )
    )
  );
  const [metricTooltip, setMetricTooltip] = useState<string | null>(null);
  const [targetDraft, setTargetDraft] = useState<{
    mode: "profit" | "margin";
    value: string;
  } | null>(null);
  const [sellingDraft, setSellingDraft] = useState<string | null>(null);
  const [pricingError, setPricingError] = useState("");
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [editingTotal, setEditingTotal] = useState(false);
  const [totalDraft, setTotalDraft] = useState<string | null>(null);
  const [discount, setDiscount] = useState(() => decimalDollars(discountCents));
  useEffect(() => {
    if (!dirty) setDiscount(decimalDollars(discountCents));
  }, [discountCents]);
  const [bodyExpanded, setBodyExpanded] = useState(initiallyExpanded);
  const [expanded, setExpanded] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const { data: settings } = useQuery(SETTINGS);
  const lastValidTotal = useRef("0");
  const change = (patch: Partial<Custom>) => {
    setItem((old) => changeCustom(old, patch, discount, lastValidTotal.current));
    setDirty(true);
    setNotice("");
  };
  const insertScopePlaceholder = (token: string) => scopeRef.current?.insert(token);
  const rowChange = (index: number, patch: Partial<CostRow>) =>
    change({ rows: item.rows.map((row, i) => (i === index ? { ...row, ...patch } : row)) });
  let totals: ReturnType<typeof customFinancials> | undefined;
  let calculationError = "";
  try {
    totals = customFinancials(item, discount);
    lastValidTotal.current = totals.total.div(100).toFixed(2);
  } catch (e) {
    calculationError = (e as Error).message;
  }
  const derivedRate =
    totals && Number(item.quantity) > 0
      ? totals.total.div(100).div(item.quantity).toDecimalPlaces(8).toString()
      : "";
  const incomplete =
    !item.name.trim() ||
    item.rows.some((row) => !row.name.trim() || !row.unit.trim()) ||
    !item.unit.trim();
  const [productDialog, setProductDialog] = useState(false);
  const [internalName, setInternalName] = useState("");
  const [productTypeId, setProductTypeId] = useState("");
  const save = async (asProduct = false, categoryId?: string) => {
    if (busy || readOnly) return { status: false, data: null };
    if (incomplete || calculationError || pricingError) {
      if (!asProduct) onSaveStatusChange?.("rejected");
      setBodyExpanded(true);
      return { status: false, data: null };
    }
    if (asProduct && !categoryId) {
      setInternalName(item.internalName || item.name);
      setProductDialog(true);
      return { status: false, data: null };
    }
    if (asProduct && !internalName.trim()) return { status: false, data: null };
    setBusy(true);
    if (!asProduct) onSaveStatusChange?.("pending");
    setError("");
    setNotice("");
    try {
      if (asProduct) {
        await onSaveProduct!(
          customInput({ ...item, internalName: internalName.trim() }),
          categoryId!
        );
        setProductDialog(false);
        setProductTypeId("");
      } else await onSave(customInput(item), totals?.discount.toFixed(0));
      if (!asProduct) {
        setDirty(false);
        onSaveStatusChange?.("fulfilled");
        setItem((old) => ({
          ...old,
          rows: old.rows.map((row) =>
            row.truckCountDefault ? { ...row, truckCountDefault: false } : row
          ),
        }));
      }
      setNotice(
        asProduct ? "Saved under the selected Product Type. This estimate remains independent." : ""
      );
      return { status: true, data: null };
    } catch (e) {
      if (!asProduct) onSaveStatusChange?.("rejected");
      setBodyExpanded(true);
      setError((e as Error).message || "Unable to save. Your changes are still here; try again.");
      return { status: false, data: null };
    } finally {
      setBusy(false);
    }
  };
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    registerSave?.(() => saveRef.current());
  }, [registerSave]);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, item, onDirtyChange]);
  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange]
  );
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const field = (label: string, key: keyof Custom, numeric = false) => (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={item[key] ?? ""}
      disabled={readOnly || busy}
      type={numeric ? "number" : "text"}
      inputProps={numeric ? customNumberProps : undefined}
      onChange={(e) => change({ [key]: e.target.value })}
    />
  );
  const addRow = (category: CostRow["category"]) =>
    change({
      rows: [
        ...item.rows,
        inheritLaborTime(item, {
          name: "",
          category,
          quantity: "1",
          unit: category === "labor" ? "Hours" : category === "trucking" ? "Hours" : "Each",
          ...(category === "trucking"
            ? { truckingUnit: "Hours" as const, trucksNeeded: "1", truckCountDefault: true }
            : {}),
          rate: "0",
          calculation: "manual",
        }),
      ],
    });
  const scopeSection = (
    <Box sx={{ px: libraryForm ? 0 : 1, py: libraryForm ? 1 : 0.5 }}>
      <UiCard variant="inner" sx={{ p: libraryForm ? 2 : 0.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography
            variant={libraryForm ? "h6" : undefined}
            sx={libraryForm ? {} : { fontSize: 14, fontWeight: 700 }}
          >
            Scope of Work
          </Typography>
          {!readOnly && (
            <TextField
              select
              size="small"
              value=""
              disabled={busy}
              sx={{ width: 170 }}
              SelectProps={{
                displayEmpty: true,
                SelectDisplayProps: { "aria-label": "Insert scope placeholder" },
                renderValue: () => "Insert placeholder",
                MenuProps: {
                  disableRestoreFocus: true,
                  TransitionProps: { onExited: () => scopeRef.current?.focus() },
                },
              }}
              onChange={(e) => insertScopePlaceholder(e.target.value)}
            >
              {scopePlaceholders.map(({ label, token }) => (
                <MenuItem key={token} value={token}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
        <CustomScopeEditor
          ref={scopeRef}
          value={item.scope ?? ""}
          format={item.scopeFormat}
          disabled={readOnly || busy}
          onChange={(scope) => change({ scope, scopeFormat: "html" })}
        />
        {scopePlaceholders.some(({ token }) => item.scope?.includes(token)) && (
          <Box sx={{ mt: 0.5, p: 1, bgcolor: draftColors.surfaceMuted, borderRadius: 1 }}>
            <Typography sx={{ fontSize: 11, color: draftColors.textSecondary }}>
              Statement of work preview
            </Typography>
            <Box
              sx={{
                fontSize: 12,
                overflowWrap: "anywhere",
                "& p": { m: 0 },
                "& ul": { my: 0, pl: 2.5 },
              }}
              dangerouslySetInnerHTML={{ __html: renderCustomScopeHtml(item) }}
            />
          </Box>
        )}
      </UiCard>
    </Box>
  );
  return (
    <EditorCard
      libraryForm={libraryForm}
      sx={{
        mb: 1.5,
        ...customFieldSx,
        ...(libraryForm
          ? {
              p: 3,
              "& .MuiInputBase-root": { fontSize: 14, minHeight: 40 },
              "& .MuiInputLabel-root": { fontSize: 14 },
            }
          : {}),
      }}
    >
      <Dialog
        open={productDialog}
        onClose={() => !busy && setProductDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Save as product</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField
            fullWidth
            required
            variant="outlined"
            size="small"
            label="Internal product name"
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 255 }}
            value={internalName}
            disabled={busy}
            onChange={(event) => setInternalName(event.target.value)}
            helperText="Shown in your product library. The customer-facing item name stays unchanged."
            sx={{
              mb: 2,
              "& .MuiInputBase-root": { height: 44, fontSize: 14 },
              "& .MuiInputLabel-root": { fontSize: 14 },
            }}
          />
          {productDialog && (
            <CustomProductTypeSelect
              value={productTypeId}
              onChange={setProductTypeId}
              disabled={busy}
            />
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <UiButton uiVariant="outlined" disabled={busy} onClick={() => setProductDialog(false)}>
            Cancel
          </UiButton>
          <UiButton
            uiVariant="primary-blue"
            disabled={busy || !productTypeId || !internalName.trim()}
            onClick={() => save(true, productTypeId)}
          >
            Save product
          </UiButton>
        </DialogActions>
      </Dialog>
      {totals?.costsKnown && totals.profit.isNegative() && (
        <Alert
          severity="warning"
          sx={{
            mx: 1,
            my: 0.5,
            px: 1,
            py: 0,
            fontSize: 12,
            alignItems: "center",
            "& .MuiAlert-message": { py: 0.5 },
            "& .MuiAlert-icon": { py: 0.5, mr: 0.75, fontSize: 18 },
          }}
        >
          Selling total is {money(totals.profit.abs())} below cost. You can save this draft, but
          must cover costs before sending.
        </Alert>
      )}
      {libraryForm && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Product details
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
              mb: 2,
            }}
          >
            {field("Internal product name", "internalName")}
            {field("Customer-facing item name", "name")}
            <TextField
              fullWidth
              label="Product Type"
              value={productTypeName ?? ""}
              InputProps={{ readOnly: true }}
            />
          </Box>
          {scopeSection}
        </Box>
      )}
      <Box
        component="header"
        sx={{
          ...(libraryForm ? {} : customHeaderFieldSx),
          p: libraryForm ? 0 : 1.5,
          ...(sharedColumnHeaders
            ? {
                py: { xs: 1.5, lg: 1 },
                "& .MuiInputLabel-root": { display: { xs: "block", lg: "none" } },
                "& .MuiOutlinedInput-notchedOutline": { top: { lg: 0 } },
                "& .MuiOutlinedInput-notchedOutline legend": { display: { lg: "none" } },
              }
            : {}),
          bgcolor: libraryForm ? "transparent" : draftColors.surfaceMuted,
          borderBottom: libraryForm ? 0 : `1px solid ${draftColors.border}`,
          mb: libraryForm ? 3 : 0,
        }}
      >
        {libraryForm && (
          <Typography variant="h5" sx={{ mb: 2 }}>
            Pricing defaults
          </Typography>
        )}
        <Box
          sx={{
            ...(libraryForm
              ? {
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 3,
                }
              : customSellingGrid),
            "& > :not(:first-child):not(:nth-child(3)) input": { textAlign: "right" },
          }}
        >
          {!libraryForm && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <IconButton
                size="small"
                sx={{ flexShrink: 0 }}
                aria-label={bodyExpanded ? "Collapse line item" : "Expand line item"}
                aria-expanded={bodyExpanded}
                onClick={() => setBodyExpanded(!bodyExpanded)}
              >
                <ExpandMore
                  sx={{ fontSize: 20, transform: bodyExpanded ? "none" : "rotate(-90deg)" }}
                />
              </IconButton>
              <Box sx={{ minWidth: 0, flex: 1 }}>{field("Item Name", "name")}</Box>
            </Box>
          )}
          {field("Quantity", "quantity", true)}
          <TextField
            select
            size="small"
            fullWidth
            label="Unit"
            SelectProps={{ renderValue: (value) => sellingUnitLabel(String(value)) }}
            value={item.unit}
            disabled={readOnly || busy}
            onChange={(e) => {
              const unit = e.target.value;
              const measured = measurementUnit(unit);
              change({
                unit,
                areaSource: measured && measured !== "lin_ft" ? "selling_quantity" : "manual",
                rows: item.rows.map((row) =>
                  row.calculation === "coverage"
                    ? {
                        ...row,
                        factorAreaUnit:
                          measured === "lin_ft"
                            ? "lin_ft"
                            : row.factorAreaUnit === "lin_ft"
                            ? "sq_ft"
                            : row.factorAreaUnit,
                      }
                    : row
                ),
              });
            }}
          >
            {!sellingUnits.includes(item.unit) && (
              <MenuItem value={item.unit} disabled>
                {sellingUnitLabel(item.unit)}
              </MenuItem>
            )}
            {sellingUnits.map((unit) => (
              <MenuItem key={unit} value={unit}>
                {sellingUnitLabel(unit)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            fullWidth
            label="Selling Price ($)"
            type="text"
            inputProps={{ inputMode: "decimal" }}
            overridden={item.pricingOverrideField === "rate"}
            explanation={
              item.pricingOverrideField === "rate"
                ? "Entered value. Other amounts adjust to maintain it."
                : ""
            }
            error={!!pricingError && sellingDraft !== null}
            disabled={readOnly || busy}
            value={sellingDraft !== null ? sellingDraft : derivedRate}
            onBlur={() => {
              if (!pricingError) setSellingDraft(null);
            }}
            onChange={(e) => {
              const value = e.target.value.replace(/[$,]/g, "");
              setSellingDraft(value);
              try {
                change(manualPricing(item, "rate", value, discount));
                setTotalDraft(null);
                setPricingError("");
                setTargetDraft(null);
              } catch (e) {
                setPricingError((e as Error).message);
                setDirty(true);
              }
            }}
          />
          {(["overheadPercentage", "indirectPercentage"] as const).map((field) => (
            <Tooltip
              key={field}
              title={
                totals
                  ? `${field === "overheadPercentage" ? "Overhead" : "Indirect"}: ${money(
                      field === "overheadPercentage" ? totals.overhead : totals.indirect
                    )}`
                  : "Enter valid pricing inputs to calculate this cost."
              }
              open={metricTooltip === field}
              onOpen={() => setMetricTooltip(field)}
              onClose={() => setMetricTooltip(null)}
              disableTouchListener
            >
              <TextField
                size="small"
                fullWidth
                label={field === "overheadPercentage" ? "Overhead %" : "Indirect %"}
                value={item[field] ?? "0"}
                error={!/^(?:\d{1,10}(?:\.\d{0,8})?|\.\d{1,8})$/.test(item[field] ?? "0")}
                helperText={
                  !/^(?:\d{1,10}(?:\.\d{0,8})?|\.\d{1,8})$/.test(item[field] ?? "0")
                    ? "Enter a nonnegative number."
                    : undefined
                }
                disabled={readOnly || busy}
                inputProps={{ inputMode: "decimal" }}
                onFocus={() => setMetricTooltip(field)}
                onClick={() => setMetricTooltip(field)}
                onBlur={() => setMetricTooltip(null)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setMetricTooltip(null);
                }}
                onChange={(e) => change({ [field]: e.target.value })}
              />
            </Tooltip>
          ))}
          <Tooltip
            title="Direct costs + overhead + indirect."
            open={metricTooltip === "costs"}
            onOpen={() => setMetricTooltip("costs")}
            onClose={() => setMetricTooltip(null)}
            disableTouchListener
          >
            <TextField
              size="small"
              fullWidth
              label="Costs ($)"
              onFocus={() => setMetricTooltip("costs")}
              onClick={() => setMetricTooltip("costs")}
              onBlur={() => setMetricTooltip(null)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setMetricTooltip(null);
              }}
              sx={customCalculatedFieldSx}
              value={
                totals?.costsKnown === false
                  ? "Not entered"
                  : totals
                  ? formatDollarAmount(totals.cost.div(100).toFixed(2))
                  : "—"
              }
              InputProps={{ readOnly: true }}
            />
          </Tooltip>
          {(["profit", "margin"] as const).map((mode) => {
            const active = item.pricing?.mode === mode;
            const editing = targetDraft?.mode === mode;
            const display =
              mode === "profit"
                ? formatDollarAmount(totals?.profit.div(100).toFixed(2) ?? "0")
                : `${totals?.marginPercentage.toFixed(2) ?? "0.00"}%`;
            return (
              <TextField
                key={mode}
                size="small"
                fullWidth
                preserveBlank
                label={mode === "profit" ? "Profit ($)" : "Margin (%)"}
                inputProps={{ inputMode: "decimal" }}
                InputProps={{ readOnly: readOnly || !item.rows.length }}
                disabled={busy}
                overridden={active}
                explanation={
                  !item.rows.length
                    ? "Add costs to calculate profit and margin."
                    : active
                    ? "Entered value. Other amounts adjust to maintain it."
                    : "Enter a value to maintain this profit as costs change."
                }
                error={editing && !!pricingError}
                value={editing ? targetDraft.value : totals?.costsKnown ? display : "—"}
                onFocus={() => {
                  if (!readOnly && item.rows.length && !targetDraft)
                    setTargetDraft({
                      mode,
                      value: active
                        ? item.pricing!.value
                        : mode === "profit"
                        ? totals?.profit.div(100).toFixed(2) ?? ""
                        : totals?.marginPercentage.toFixed(2) ?? "",
                    });
                }}
                onBlur={() => {
                  if (!pricingError) setTargetDraft(null);
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/[$,%]/g, "");
                  setTargetDraft({ mode, value });
                  try {
                    const patch = editProfitTarget(item, mode, value, discount);
                    if (Object.keys(patch).length) change(patch);
                    setSellingDraft(null);
                    setTotalDraft(null);
                    setPricingError("");
                  } catch (e) {
                    setPricingError((e as Error).message);
                    setDirty(true);
                  }
                }}
                sx={
                  totals?.profit.isNegative() && mode === "profit"
                    ? { "& input": { color: draftColors.red } }
                    : undefined
                }
              />
            );
          })}
          {!libraryForm && (
            <TextField
              size="small"
              fullWidth
              label="Discount ($)"
              type="text"
              inputProps={{ inputMode: "decimal" }}
              value={editingDiscount ? discount : formatDollarAmount(discount)}
              onFocus={() => setEditingDiscount(true)}
              onBlur={() => setEditingDiscount(false)}
              disabled={readOnly || busy || !allowDiscount}
              onChange={(e) => {
                setDiscount(e.target.value.replace(/[$,]/g, ""));
                setTotalDraft(null);
                setDirty(true);
                setNotice("");
              }}
            />
          )}
          <TextField
            size="small"
            fullWidth
            label="Total ($)"
            overridden={item.pricingOverrideField === "total"}
            explanation={
              item.pricingOverrideField === "total"
                ? "Entered value. Other amounts adjust to maintain it."
                : ""
            }
            type="text"
            inputProps={customNumberProps}
            disabled={readOnly || busy}
            value={
              editingTotal
                ? totalDraft !== null
                  ? totalDraft
                  : totals?.total.div(100).toFixed(2) ?? ""
                : formatDollarAmount(
                    totalDraft !== null ? totalDraft : totals?.total.div(100).toFixed(2) ?? ""
                  )
            }
            onFocus={() => setEditingTotal(true)}
            onBlur={() => {
              setEditingTotal(false);
              if (totals) setTotalDraft(null);
            }}
            FormHelperTextProps={{ sx: { color: draftColors.textSecondary, fontSize: 11, mx: 0 } }}
            onChange={(e) => {
              const value = e.target.value.replace(/[$,]/g, "");
              setTotalDraft(value);
              try {
                change(manualPricing(item, "fixed", value, discount));
                setSellingDraft(null);
                setPricingError("");
                setTargetDraft(null);
              } catch (e) {
                setPricingError((e as Error).message);
                setDirty(true);
                setBodyExpanded(true);
              }
            }}
          />
          {!libraryForm && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", lg: "center" },
                minHeight: 30,
              }}
            >
              {optionalControl}
            </Box>
          )}
          {libraryForm ? (
            !readOnly && item.pricingBaseline ? (
              <UiButton
                uiVariant="outlined"
                disabled={busy}
                onClick={() => {
                  const restored = resetCustomPricing(item);
                  change(restored);
                  try {
                    customFinancials(restored, discount);
                  } catch {
                    setBodyExpanded(true);
                  }
                  setSellingDraft(null);
                  setTotalDraft(null);
                  setPricingError("");
                  setTargetDraft(null);
                  setError("");
                  setMenuAnchor(null);
                }}
              >
                Reset pricing
              </UiButton>
            ) : null
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.5,
                minHeight: 30,
                gridColumn: { xs: "1 / -1", lg: "auto" },
                whiteSpace: "nowrap",
              }}
            >
              {dirty && (
                <Tooltip title="Unsaved changes — expand the item to save">
                  <Box
                    component="span"
                    aria-label="Unsaved changes"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: draftColors.blue,
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
              {!readOnly && (actions || item.pricingBaseline) && (
                <>
                  <IconButton
                    size="small"
                    disabled={busy}
                    aria-label={`Actions for ${item.name}`}
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                  >
                    <MoreHoriz fontSize="small" />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={!!menuAnchor}
                    onClose={() => setMenuAnchor(null)}
                  >
                    {item.pricingBaseline && (
                      <MenuItem
                        onClick={() => {
                          const restored = resetCustomPricing(item);
                          change(restored);
                          try {
                            customFinancials(restored, discount);
                          } catch {
                            setBodyExpanded(true);
                          }
                          setSellingDraft(null);
                          setTotalDraft(null);
                          setPricingError("");
                          setTargetDraft(null);
                          setError("");
                          setMenuAnchor(null);
                        }}
                      >
                        Reset pricing
                      </MenuItem>
                    )}
                    {actions?.(() => setMenuAnchor(null))}
                  </Menu>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Collapse in={libraryForm || bodyExpanded} unmountOnExit={false}>
        {!libraryForm && scopeSection}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            bgcolor: draftColors.surfaceMuted,
            borderTop: `1px solid ${draftColors.border}`,
            borderBottom: `1px solid ${draftColors.border}`,
            mb: 0.5,
            flexWrap: "wrap",
          }}
        >
          {libraryForm ? (
            <Typography variant="h5" sx={{ flex: 1, py: 2 }}>
              Costs and calculations
            </Typography>
          ) : (
            <ButtonBase
              aria-label="Cost Breakdown"
              aria-expanded={expanded}
              onClick={() => setExpanded(!expanded)}
              sx={{
                flex: 1,
                justifyContent: "flex-start",
                gap: 0.75,
                py: 0.5,
                minWidth: 160,
                borderRadius: 1,
                "&.Mui-focusVisible": {
                  outline: `2px solid ${draftColors.blue}`,
                  outlineOffset: -2,
                },
              }}
            >
              <ExpandMore sx={{ fontSize: 20, transform: expanded ? "rotate(180deg)" : "none" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Cost Breakdown</Typography>
            </ButtonBase>
          )}
          <Typography sx={{ fontSize: 12, color: draftColors.textSecondary }}>
            {pricingStatus(item)}
          </Typography>
        </Box>
        <Collapse in={libraryForm || expanded} unmountOnExit={false}>
          <Box
            sx={{
              p: libraryForm ? 2 : 0.5,
              display: "grid",
              gap: libraryForm ? 2 : 0.5,
              bgcolor: libraryForm ? "transparent" : draftColors.pageBg,
            }}
          >
            {(["labor", "material", "trucking", "equipment", "other"] as const).map((category) => (
              <CustomCostSection
                key={category}
                category={category}
                item={item}
                onItemChange={change}
                onCalculations={(tab, rowIndex) => setCalculationsOpen({ tab, rowIndex })}
                rows={item.rows}
                totals={totals}
                readOnly={readOnly}
                busy={busy}
                settings={settings}
                onChange={rowChange}
                onAdd={() => addRow(category)}
                onDelete={(index) => {
                  const id = item.rows[index].rowId;
                  if (
                    id &&
                    (item.helpers?.production?.materialRowId === id ||
                      item.rows.some((r) => r.hauling?.materialRowId === id))
                  )
                    setDeletingRow(index);
                  else change({ rows: item.rows.filter((_, i) => i !== index) });
                }}
              />
            ))}
          </Box>
        </Collapse>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0, display: "grid", gap: 1 }}>
          {calculationError && <Alert severity="warning">{calculationError}</Alert>}
          {pricingError && <Alert severity="warning">{pricingError}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {notice && <Alert severity="success">{notice}</Alert>}
          {!readOnly && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
              {libraryForm ? (
                <MDButton
                  variant="gradient"
                  color="success"
                  disabled={busy || incomplete || !!calculationError || !!pricingError}
                  onClick={() => save()}
                >
                  {busy ? "Saving…" : saveLabel}
                </MDButton>
              ) : (
                <UiButton
                  uiVariant="primary-green"
                  disabled={busy || incomplete || !!calculationError || !!pricingError}
                  onClick={() => save()}
                >
                  {busy ? "Saving…" : saveLabel}
                </UiButton>
              )}

              {onUpdateProduct && item.sourceProductId && (
                <UiButton
                  uiVariant="outlined-small"
                  disabled={busy || incomplete || !!calculationError || !!pricingError}
                  onClick={() => setUpdatingLibrary(true)}
                >
                  Update library product
                </UiButton>
              )}
              {onSaveProduct && (
                <UiButton
                  uiVariant="outlined-small"
                  disabled={busy || incomplete || !!calculationError || !!pricingError}
                  onClick={() => save(true)}
                >
                  {item.calculationVersion === 2 ? "Save as new product" : "Save as product"}
                </UiButton>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
      <Dialog open={updatingLibrary} onClose={() => setUpdatingLibrary(false)}>
        <DialogTitle>Update library product</DialogTitle>
        <DialogContent>
          Update the linked library product #{item.sourceProductId} with “
          {item.internalName || item.name}”. This affects future insertions only; other proposals
          retain their snapshots.
        </DialogContent>
        <DialogActions>
          <UiButton
            uiVariant="outlined-small"
            disabled={busy}
            onClick={() => setUpdatingLibrary(false)}
          >
            Cancel
          </UiButton>
          <UiButton
            uiVariant="primary-green"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                const revision = await onUpdateProduct!(item);
                change({ sourceProductRevision: revision });
                setUpdatingLibrary(false);
                setNotice("Library product updated. Save this item to retain the new reference.");
              } catch (e) {
                setError((e as Error).message);
                setUpdatingLibrary(false);
              } finally {
                setBusy(false);
              }
            }}
          >
            Update library product
          </UiButton>
        </DialogActions>
      </Dialog>
      {calculationsOpen !== null && (
        <CustomCalculationsDrawer
          initialTab={calculationsOpen.tab}
          rowIndex={calculationsOpen.rowIndex}
          item={item}
          onApply={change}
          onClose={() => setCalculationsOpen(null)}
          readOnly={readOnly}
          settings={settings}
        />
      )}
      <Dialog open={deletingRow !== null} onClose={() => setDeletingRow(null)}>
        <DialogTitle>Retain dependent quantities?</DialogTitle>
        <DialogContent>
          Removing this source will keep the latest linked quantities as manual values. Only
          dependent calculation links will be disabled.
        </DialogContent>
        <DialogActions>
          <UiButton uiVariant="outlined-small" onClick={() => setDeletingRow(null)}>
            Cancel
          </UiButton>
          <UiButton
            uiVariant="primary-green"
            disabled={!totals}
            onClick={() => {
              try {
                change(removeSourceRow(item, deletingRow!, (r) => rowQuantity(item, r).quantity));
                setDeletingRow(null);
              } catch (e) {
                setError((e as Error).message);
              }
            }}
          >
            Keep values and delete
          </UiButton>
        </DialogActions>
      </Dialog>
    </EditorCard>
  );
}
